import { ChangeDetectionStrategy, Component, computed, ElementRef, input, model, signal, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { bzIconSearchOutline } from '@bringoz/ui/icons';
import type { BzIconDef } from '../icon/bz-icon-def';
import { BzIcon } from '../icon/bz-icon';

/** `sm` is the compact panel: 11px rows. `md` is the default 12px. */
export type BzSelectSize = 'sm' | 'md';

/** One choice. `label` is what the user reads; `value` is what the caller gets back. */
export interface BzSelectOption<T = unknown> {
  value: T;
  label: string;
  disabled?: boolean;
  /** Icon before the label. */
  icon?: BzIconDef;
  /** Trailing hint, rendered italic and muted — a keyboard shortcut, a count, a unit. */
  hint?: string;
  /** Icon at the trailing edge, after the hint. A chevron for a row that opens something. */
  trailingIcon?: BzIconDef;
}

/** A titled run of options. The title is a label, not a choice: it cannot be selected. */
export interface BzSelectGroup<T = unknown> {
  label: string;
  options: readonly BzSelectOption<T>[];
}

/** `options` takes either shape, mixed freely — a flat list needs no grouping ceremony. */
export type BzSelectItem<T = unknown> = BzSelectOption<T> | BzSelectGroup<T>;

function isGroup<T>(item: BzSelectItem<T>): item is BzSelectGroup<T> {
  return 'options' in item;
}

/** Narrows the two-shaped `value` without an `as` cast at every call site. */
function isMultiValue<T>(value: T | readonly T[] | null): value is readonly T[] {
  return Array.isArray(value);
}

/**
 * Keys the open panel still needs while the filter field holds focus.
 * Anything else is a character the field should receive, so its keydown stops
 * there rather than reaching `mat-select`'s panel handler: otherwise typing
 * "de" runs Material's own typeahead instead of filtering, and the space bar
 * toggles the active row instead of typing a space.
 *
 * `Escape` is in the list even though it never reaches the panel — Material
 * closes on Escape from the overlay's document-level keydown stream, which a
 * stopped event does not reach either.
 */
const PANEL_KEYS: ReadonlySet<string> = new Set([
  'ArrowDown',
  'ArrowUp',
  'Enter',
  'Escape',
  'Tab',
  'PageDown',
  'PageUp',
]);

/**
 * A dropdown, built on `mat-select`.
 *
 * Material rather than hand-built for one reason: the accessible behaviour of
 * a listbox is the expensive part — overlay positioning, roving focus,
 * typeahead, `aria-activedescendant`, and returning focus to the trigger on
 * close. That is what `mat-select` already gets right.
 *
 * It looks like Bringoz rather than like Material because
 * `styles/material.css` maps Material's `--mat-sys-*` tokens onto ours, and
 * because the panel itself is restyled here to the design's own chrome. A
 * consumer that forgets to import it gets a working but Material-coloured
 * control.
 *
 * Options arrive as data through an input rather than as projected content.
 * That keeps the value round-trip typed, and it is what the console's existing
 * `app-bringoz-select` already does, so migrating a call site is a rename
 * rather than a rewrite. The same input takes groups, so grouping costs a
 * caller nothing until it needs it.
 */
@Component({
  selector: 'bz-select',
  imports: [FormsModule, MatFormFieldModule, MatSelectModule, NgTemplateOutlet, BzIcon],
  templateUrl: './bz-select.html',
  styleUrl: './bz-select.scss',
  // `size` has to reach the host, because the trigger's height is set through
  // Material's density custom properties and those have to be declared on an
  // ancestor of the form field.
  host: {
    '[class.bz-select--sm]': "size() === 'sm'",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzSelect<T> {
  readonly options = input<readonly BzSelectItem<T>[]>([]);

  /** Single value, or an array when `multiple` is set. */
  readonly value = model<T | readonly T[] | null>(null);
  readonly disabled = model(false);

  readonly multiple = input(false);
  readonly placeholder = input('');

  /** Floating label above the field. Leave unset for a bare control. */
  readonly label = input('');

  /**
   * `sm` is the compact control: a 30px field over 11px panel rows. `md` is
   * the design's default — a 40px field over 12px rows.
   */
  readonly size = input<BzSelectSize>('md');

  /**
   * Adds a filter field at the top of the panel, matched against option
   * labels. Worth turning on somewhere around twenty options; below that the
   * field costs a row of panel height and buys nothing.
   */
  readonly searchable = input(false);

  readonly searchPlaceholder = input('Search');

  /** Shown in place of the rows when the filter matches nothing. */
  readonly noResultsLabel = input('No results');

  /**
   * Accessible name for the control. Unnecessary when `label` is set — that
   * already names it, and a second name only competes with the first.
   */
  readonly ariaLabel = input('');

  /**
   * The panel is rendered in an overlay at the document root, outside this
   * component's view, so its styles cannot be encapsulated with the rest. The
   * class is how the global rules in bz-select.scss find it.
   */
  protected readonly panelClass = computed(() =>
    this.size() === 'sm' ? 'bz-select-panel bz-select-panel--sm' : 'bz-select-panel',
  );

  protected readonly searchIcon = bzIconSearchOutline;

  /** What the filter field currently holds. Cleared when the panel closes. */
  protected readonly query = signal('');

  private readonly searchField = viewChild<ElementRef<HTMLInputElement>>('searchField');

  /**
   * One uniform shape for the template: every option belongs to a run, and an
   * ungrouped run carries a null label. Consecutive loose options stay in a
   * single run so they do not each get their own `mat-optgroup` wrapper, which
   * would put spacing between them.
   */
  private readonly runs = computed<readonly { label: string | null; options: readonly BzSelectOption<T>[] }[]>(() => {
    const runs: { label: string | null; options: BzSelectOption<T>[] }[] = [];

    for (const item of this.options()) {
      if (isGroup(item)) {
        runs.push({ label: item.label, options: [...item.options] });
        continue;
      }

      const last = runs[runs.length - 1];
      if (last && last.label === null) {
        last.options.push(item);
      } else {
        runs.push({ label: null, options: [item] });
      }
    }

    return runs;
  });

  /**
   * What the panel actually renders. A group whose every option is filtered
   * out drops its title too — a heading over nothing reads as a bug.
   */
  protected readonly visibleRuns = computed(() => {
    const query = this.query().trim().toLowerCase();
    if (!query) return this.runs();

    return this.runs()
      .map((run) => ({
        label: run.label,
        options: run.options.filter((option) => option.label.toLowerCase().includes(query)),
      }))
      .filter((run) => run.options.length > 0);
  });

  private readonly visibleValues = computed(
    () => new Set(this.visibleRuns().flatMap((run) => run.options.map((option) => option.value))),
  );

  /** Declaration order, so a reconciled multi-select value is ordered the way the caller wrote its options. */
  private readonly declaredValues = computed(() =>
    this.runs().flatMap((run) => run.options.map((option) => option.value)),
  );

  protected onValueChange(next: T | readonly T[] | null): void {
    if (this.multiple() && isMultiValue(next)) {
      this.value.set(this.withFilteredOut(next));
      // Material refocuses its own trigger after every click in a multiple
      // select (MatSelect#_onSelect), which would take the caret out of the
      // filter field after the first tick. Putting it back is what makes
      // "filter, tick, filter, tick" work.
      this.focusSearchField();
      return;
    }

    this.value.set(next);
  }

  /**
   * `mat-form-field` ends its own container-click handler in
   * `MatSelect#focus`, which pulls focus back to the trigger on the very
   * gesture that opened the panel — so it fights the filter field for it, and
   * wins or loses depending on where Angular happens to flush effects. The
   * panel is open by the time the click reaches the form field, so the only
   * thing dropped here is that redundant refocus. Clicks on the label and the
   * outline, which are outside `mat-select`, still reach it.
   */
  protected onSelectClick(event: MouseEvent): void {
    if (this.searchable()) event.stopPropagation();
  }

  protected onOpenedChange(opened: boolean): void {
    if (opened) {
      this.focusSearchField();
      return;
    }

    // A query left behind would keep the selected row out of the DOM, and the
    // trigger reads its text from the options it has rendered — so the closed
    // field would show a placeholder over a value that is still set.
    this.query.set('');
  }

  protected onSearchInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    if (!PANEL_KEYS.has(event.key)) event.stopPropagation();
  }

  /**
   * `mat-select` reports a multi-select value by reading the options it has
   * rendered (`MatSelect#_propagateChanges`), so every selection the filter is
   * hiding is absent from what it emits. Ticking one row under an active
   * filter would therefore clear all the others. This puts them back.
   *
   * A value the user has just unticked was on screen to be clicked, so it is
   * visible by definition and never resurrected here.
   */
  private withFilteredOut(next: readonly T[]): readonly T[] {
    const previous = this.value();
    if (!isMultiValue(previous)) return next;

    const visible = this.visibleValues();
    const hidden = previous.filter((value) => !visible.has(value));
    if (hidden.length === 0) return next;

    const order = this.declaredValues();
    return [...hidden, ...next].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  private focusSearchField(): void {
    this.searchField()?.nativeElement.focus();
  }
}
